<template>
	<ParamWrapper v-bind="wrapperProps">
		<div class="params_list_container">
			<v-row no-gutters class="mt-0 px-0">
				<v-col col="12">
					<ListSourceParamType @update-input-field="updateInputField" v-bind="componentProps" :param="sourceParam" />
				</v-col>
			</v-row>
			<v-row v-for="listParam in param.params" :key="`${node.id}_${listParam.key}`" no-gutters class="mt-0 px-0">
				<v-col col="12">
					<SubFolderIndicatorLine class="mr-1" />
					<ListParamDispatcher
						class="pl-4"
						:param="listParam"
						v-bind="componentProps"
						:items="rowsOfSource"
						:sourceOfList="sourceOfListString"
						@update-input-field="updateInputField"
						:key="buildFieldKey(listParam)"
					/>
				</v-col>
			</v-row>
		</div>
	</ParamWrapper>
</template>

<script>
import AppService from '@/utils/AppService';
import ParamTypeMixin from './mixins/ParamTypeMixin';
import ListSourceParamType from './ListParams/ListSourceParamType';
import ListParamDispatcher from './ListParams/ListParamDispatcher';
import {
	buildListenersObject,
	convertSimpleTypeArrayToObjectArray,
	filterArraysFromPreviousStepData,
	flattenPreserveArrays,
	isNullOrUndefined,
} from '@/utils/helpers';
import ParamsService from '@/utils/ParamsService';
import SubFolderIndicatorLine from '../../TableActionEditor/SubFolderIndicatorLine';
import { getPreviousStepDataForNodeByUUID } from '@/store/modules/nodes/getters';
import { mapGetters, mapActions } from 'vuex';
import isObjectLike from 'lodash/isObjectLike';
import { setTableContent, clearContent, setIsShowKnob } from '@/store/modules/sideBarDrawer/actions';
import TableActionEditorService from '../../TableActionEditor/table-action-edtior-service';
import { replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import get from 'lodash/get';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
const { PLY_PARAMS } = ParamsService;
const { listSourceParam: plyListSourceParam } = PLY_PARAMS;
export default {
	name: 'ListParamType',
	mixins: [ParamTypeMixin],
	components: { ListSourceParamType, ListParamDispatcher, SubFolderIndicatorLine },
	computed: {
		...mapGetters([
			getPreviousStepDataForNodeByUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		sourceParam() {
			return { ...this.param, ...plyListSourceParam };
		},
		componentProps() {
			return {
				...this.$attrs,
				service: this.service,
				node: this.node,
				action: this.action,
				listParamsMap: this.listParamsMap,
				listParamsMapLabels: this.listParamsMapLabels,
				readonly: this.readonly,
			};
		},
		componentListeners() {
			const UPDATE_INPUT_FIELD = 'update-input-field';
			const UPDATE_INPUT_FIELD_LABEL = 'update-input-field-label';
			const listenersFunctions = [UPDATE_INPUT_FIELD, UPDATE_INPUT_FIELD_LABEL];
			return buildListenersObject(this, listenersFunctions);
		},
		listParamsMap() {
			return this.node.params_map[this.param.key] || {};
		},
		listParamsMapLabels() {
			return this.node.params_map_labels[this.param.key] || {};
		},
		sourceOfListString() {
			return this.listParamsMap[this.sourceParam.key];
		},
		sourceOfList() {
			const obj = { path: null, nodeUUID: null };
			const source = this.sourceOfListString;
			if (!source) {
				return obj;
			}
			obj.path = ParamsService.extractPathFromValue(
				source,
				this[replaceInsertDataStringDictionary.fullPath],
				this[configurationsMappingBlocksUUIDs.fullPath]
			);
			obj.nodeUUID = ParamsService.extractNodeUUIDFromValue(source);
			return obj;
		},
		exampleRow() {
			return this.rowsOfSource.length === 0 ? {} : convertSimpleTypeArrayToObjectArray(this.rowsOfSource)[0];
		},
		complexDataTypeSource() {
			return this.rowsOfSource.some((value) => typeof value === 'object');
		},
		items() {
			const items = [];
			if (this.complexDataTypeSource) {
				for (const [key, value] of Object.entries(this.exampleRow)) {
					items.push({
						key,
						example: isObjectLike(value) ? JSON.stringify(value) : value,
					});
				}
			} else {
				items.push({ key: 'value', example: this.exampleRow }); // explain the user there is single option, ignored by sdk
			}
			return items;
		},
		rowsOfSource() {
			const source = this.listParamsMap[this.sourceParam.key];
			if (!source) {
				return [];
			}
			const { path: sourcePath, nodeUUID: sourceNodeUUID } = this.sourceOfList;
			let previousStepData = this[getPreviousStepDataForNodeByUUID.fullPath](sourceNodeUUID);
			if (!sourceNodeUUID || isNullOrUndefined(previousStepData)) {
				return [];
			}
			previousStepData = filterArraysFromPreviousStepData(previousStepData);
			const listOfRows = get(previousStepData, sourcePath);
			return listOfRows || [];
		},
		rowsFromPreviousSteps() {
			const value = this.listParamsMap[this.sourceParam.key];
			if (!value) {
				return [];
			}
			return flattenPreserveArrays(
				TableActionEditorService.rowsFromPreviousSteps(
					value,
					this[getPreviousStepDataForNodeByUUID.fullPath],
					this[replaceInsertDataStringDictionary.fullPath],
					this[configurationsMappingBlocksUUIDs.fullPath]
				)
			);
		},
	},
	methods: {
		...mapActions([setTableContent.fullPath, clearContent.fullPath, setIsShowKnob.fullPath]),
		updateInputField(payload) {
			const { map /*, label*/, param } = payload;

			if (param.key === plyListSourceParam.key) {
				this.param.params.forEach((parameter) => {
					this.updateInputField({
						map: { key: parameter.key, value: '' } /*, label: { key: parameter.key, value: '' }*/,
						param: parameter,
					});
				});
			}

			const newValue = Object.assign({}, this.listParamsMap, { [map.key]: map.value });

			this.localValue = newValue;
		},
		buildFieldKey(param) {
			return ParamsService.buildFieldKey(param, this.listParamsMap);
		},
		setSideBarTableViewContent() {
			TableActionEditorService.setSideBarTableViewContent(this.rowsFromPreviousSteps, this.param.name, this[setTableContent.fullPath]);
		},
		updateSideBarTableViewContent() {
			const value = this.rowsFromPreviousSteps;
			if (value) {
				this.setSideBarTableViewContent();
			} else {
				this.clearContentOfSideBar();
			}
			clearTimeout(this.setShowKnobTimeout);
			this.setShowKnobTimeout = setTimeout(() => this[setIsShowKnob.fullPath](!!value), 1000);
		},
		clearContentOfSideBar() {
			this[clearContent.fullPath]();
		},
	},
	beforeDestroy() {
		clearTimeout(this.setShowKnobTimeout);
		this[setIsShowKnob.fullPath](false);
		this.clearContentOfSideBar();
	},
	watch: {
		rowsFromPreviousSteps: {
			handler() {
				if (AppService.isSlugOfTables(this.service.service_slug)) {
					this.updateSideBarTableViewContent();
				}
			},
			immediate: true,
			deep: true,
		},
	},
	data() {
		return { setShowKnobTimeout: null };
	},
};
</script>
<style lang="scss" scoped></style>
